import { useEffect, useState, ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Stack,
  Autocomplete,
  Box,
  Container,
  Grid,
  Button,
  InputAdornment,
} from "@mui/material";

import Logo from "../assets/img/logo.svg";

import { NavigationComponent } from "./navigation";

import {
  campusService,
  countryService,
  IStudentService,
  studentService,
} from "../modules/service";

import { useAppSelector, useAppDispatch } from "../redux/hooks";
import { getApp, country, campus, backdrop, message } from "../redux/app.slice";

interface IError {
  campus: boolean;
  sid: boolean;
  country: boolean;
  phone: boolean;
}

export const RegisterComponent = () => {
  const navigate = useNavigate();
  const app = useAppSelector(getApp);
  const dispatch = useAppDispatch();

  const [Phone, SetPhone] = useState("");

  const [Student, SetStudent] = useState<IStudentService>({
    campus: "",
    sid: "",
    country: "",
    phone: "",
  });

  const [Error, SetError] = useState<IError>({
    campus: false,
    sid: false,
    country: false,
    phone: false,
  });

  useEffect(() => {
    if (app.campuses) {
      if (app.campuses.length === 0) {
        dispatch(backdrop(true));
        campusService()
          .then((response) => {
            dispatch(campus(response.data));
          })
          .catch((error) => console.log(error))
          .finally(() => dispatch(backdrop(false)));
      }
    }
  }, [app.campuses, dispatch]);

  useEffect(() => {
    if (app.countries) {
      if (app.countries.length === 0) {
        dispatch(backdrop(true));
        countryService()
          .then((response) => {
            dispatch(country(response.data));
          })
          .catch((error) => console.log(error))
          .finally(() => dispatch(backdrop(false)));
      }
    }
  }, [app.countries, dispatch]);

  const HandleChange = (event: ChangeEvent<HTMLInputElement>) => {
    SetStudent({ ...Student, [event.target.name]: event.target.value });
  };

  const HandleValidate = () => {
    const error: IError = {
      campus: false,
      sid: false,
      country: false,
      phone: false,
    };

    if (Student.campus === "") error.campus = true;
    else error.campus = false;

    if (Student.sid === "") error.sid = true;
    else error.sid = false;

    if (Student.country === "") error.country = true;
    else error.country = false;

    if (Student.phone === "") error.phone = true;
    else error.phone = false;

    SetError(error);

    return Object.values(error).every((value) => value === false);
  };

  const HandleSubmit = () => {
    if (HandleValidate()) {
      dispatch(backdrop(true));
      studentService({
        campus: Student.campus,
        sid: Student.sid,
        country: Student.country,
        phone: Student.phone,
      })
        .then((response) => {
          dispatch(
            message({
              open: true,
              severity: "success",
              message:
                "Student registered successfully. Please login to continue.",
            })
          );
          navigate("/");
        })
        .catch((error) => {
          const error_code = error.response.data.error;
          if (error_code === "NOT_FOUND")
            dispatch(
              message({
                open: true,
                severity: "error",
                message:
                  "Sorry the student is not found in the database. Please try again.",
              })
            );
          else if (
            ["STUDENT_UNVERIFIED", "ALREADY_REGISTERED"].includes(error_code)
          ) {
            dispatch(
              message({
                open: true,
                severity: "error",
                message:
                  "You are already registered. Please login to continue.",
              })
            );
            navigate("/");
          }
        })
        .finally(() => dispatch(backdrop(false)));
    }
  };

  return (
    <>
      <NavigationComponent />
      <Container maxWidth="sm" style={{ margin: "2em auto" }}>
        <Stack spacing={2}>
          <Grid
            container
            spacing={0}
            direction="column"
            alignItems="center"
            justifyContent="center"
            style={{ marginBottom: "1em" }}
          >
            <img
              src={Logo}
              width={100}
              alt="London School of Commerce Group of Colleges Logo"
            />
          </Grid>

          <Autocomplete
            options={app.campuses}
            autoHighlight
            onChange={(event, value) => {
              if (value) SetStudent({ ...Student, campus: value.id });
            }}
            size="small"
            getOptionLabel={(option) => option.name}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.name}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                error={Error.campus}
                size="small"
                label="Campus"
                name="campus"
                helperText="Select your campus"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: "new-password",
                }}
              />
            )}
          />

          <TextField
            name="sid"
            label="Student ID"
            value={Student.sid}
            onChange={HandleChange}
            helperText="Enter your student ID"
            fullWidth
            size="small"
            error={Error.sid}
          />

          <Autocomplete
            options={app.countries}
            onChange={(event, value) => {
              if (value) {
                SetStudent({ ...Student, country: value.id });
                SetPhone(`+${value.phone}`);
              }
            }}
            autoHighlight
            size="small"
            getOptionLabel={(option) => `${option.name} (+${option.phone})`}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.name} (+{option.phone})
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                error={Error.country}
                label="Country"
                name="country"
                helperText="Select your country"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: "new-password",
                }}
              />
            )}
          />
          <TextField
            name="phone"
            label="Phone"
            error={Error.phone}
            value={Student.phone}
            onChange={HandleChange}
            helperText="Enter your phone number"
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{Phone}</InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            disableElevation
            onClick={HandleSubmit}
            size="large"
          >
            Submit
          </Button>
        </Stack>
      </Container>
    </>
  );
};
